import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Calendar,
  ArrowLeft,
  Clock,
  MapPin,
  Users,
  Check,
  ChevronRight,
  ChevronLeft,
  Car,
  Building2,
  Plus,
  Trash2,
  Edit2,
  Loader2,
  CalendarDays,
  List,
  Filter,
  User,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths, parseISO, isToday } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResidentCalendar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [calendarItems, setCalendarItems] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' | 'list'
  const [filterType, setFilterType] = useState('all'); // 'all' | 'event' | 'booking'
  
  // Event management (for trustees)
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_type: 'general',
    start_time: '',
    end_time: '',
    location: '',
  });

  const hoaId = user?.hoa_id || 'demo_hoa';
  const canManageEvents = ['trustee', 'property_manager', 'managing_agent', 'agent_admin', 'super_agent', 'platform_owner'].includes(user?.role);

  const getBackPath = () => {
    if (['trustee', 'property_manager'].includes(user?.role)) return '/admin';
    if (['managing_agent', 'agent_admin'].includes(user?.role)) return '/agent';
    if (user?.role === 'platform_owner') return '/platform';
    return '/dashboard';
  };

  useEffect(() => {
    fetchCalendarData();
  }, [currentMonth]);

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const startDate = startOfMonth(currentMonth).toISOString();
      const endDate = endOfMonth(addMonths(currentMonth, 1)).toISOString();
      
      const response = await fetch(
        `${API_URL}/api/events/calendar/${hoaId}?start_date=${startDate}&end_date=${endDate}&include_bookings=true`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setCalendarItems(data);
      } else {
        // Demo data
        setCalendarItems([
          {
            id: 'evt_1',
            type: 'event',
            event_type: 'agm',
            title: 'Annual General Meeting',
            description: 'Yearly AGM to discuss budget and elections',
            start: new Date(Date.now() + 86400000 * 5).toISOString(),
            end: new Date(Date.now() + 86400000 * 5 + 7200000).toISOString(),
            location: 'Clubhouse',
            color: '#8b5cf6'
          },
          {
            id: 'evt_2',
            type: 'event',
            event_type: 'community',
            title: 'Community BBQ',
            start: new Date(Date.now() + 86400000 * 10).toISOString(),
            end: new Date(Date.now() + 86400000 * 10 + 14400000).toISOString(),
            location: 'Pool Area',
            color: '#10b981'
          },
          {
            id: 'bkg_1',
            type: 'booking',
            booking_type: 'visitor_parking',
            title: '🚗 Parking: VP1 - Unit 12A',
            description: 'Vehicle: CA 123 456',
            start: new Date(Date.now() + 86400000 * 2).toISOString(),
            end: new Date(Date.now() + 86400000 * 3).toISOString(),
            status: 'confirmed',
            color: '#10b981'
          },
        ]);
      }
    } catch (error) {
      console.error('Error fetching calendar:', error);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventForm.title || !eventForm.start_time || !eventForm.end_time) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/events/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          hoa_id: hoaId,
          ...eventForm
        })
      });

      if (response.ok) {
        toast.success('Event created successfully');
        setShowEventDialog(false);
        resetEventForm();
        fetchCalendarData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create event');
      }
    } catch (error) {
      toast.error('Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateEvent = async () => {
    if (!selectedItem || selectedItem.type !== 'event') return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/events/${selectedItem.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(eventForm)
      });

      if (response.ok) {
        toast.success('Event updated');
        setShowEventDialog(false);
        setSelectedItem(null);
        resetEventForm();
        fetchCalendarData();
      } else {
        toast.error('Failed to update event');
      }
    } catch (error) {
      toast.error('Failed to update event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedItem || selectedItem.type !== 'event') return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/events/${selectedItem.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Event deleted');
        setShowDeleteConfirm(false);
        setSelectedItem(null);
        fetchCalendarData();
      } else {
        toast.error('Failed to delete event');
      }
    } catch (error) {
      toast.error('Failed to delete event');
    } finally {
      setSubmitting(false);
    }
  };

  const resetEventForm = () => {
    setEventForm({
      title: '',
      description: '',
      event_type: 'general',
      start_time: '',
      end_time: '',
      location: '',
    });
  };

  const openEditDialog = (item) => {
    if (item.type !== 'event') {
      toast.info('Bookings can be managed from the Bookings page');
      return;
    }
    setSelectedItem(item);
    setEventForm({
      title: item.title,
      description: item.description || '',
      event_type: item.event_type || 'general',
      start_time: item.start ? item.start.slice(0, 16) : '',
      end_time: item.end ? item.end.slice(0, 16) : '',
      location: item.location || '',
    });
    setShowEventDialog(true);
  };

  const getItemsForDate = (date) => {
    return calendarItems.filter(item => {
      const itemDate = parseISO(item.start);
      return isSameDay(itemDate, date);
    });
  };

  const filteredItems = calendarItems.filter(item => {
    if (filterType === 'all') return true;
    return item.type === filterType;
  });

  const selectedDateItems = getItemsForDate(selectedDate);

  const getEventTypeBadge = (type) => {
    const badges = {
      agm: <Badge className="bg-purple-100 text-purple-700">AGM</Badge>,
      community: <Badge className="bg-emerald-100 text-emerald-700">Community</Badge>,
      maintenance: <Badge className="bg-amber-100 text-amber-700">Maintenance</Badge>,
      security_drill: <Badge className="bg-red-100 text-red-700">Security</Badge>,
      meeting: <Badge className="bg-blue-100 text-blue-700">Meeting</Badge>,
      general: <Badge className="bg-slate-100 text-slate-700">General</Badge>,
    };
    return badges[type] || badges.general;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="w-3 h-3 mr-1" />Pending</Badge>,
      confirmed: <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Confirmed</Badge>,
      paid: <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Paid</Badge>,
    };
    return badges[status] || <Badge variant="secondary">{status}</Badge>;
  };

  const formatTime = (dateStr) => {
    return format(parseISO(dateStr), 'HH:mm');
  };

  const formatDateRange = (start, end) => {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    if (isSameDay(startDate, endDate)) {
      return `${format(startDate, 'MMM d')} • ${format(startDate, 'HH:mm')} - ${format(endDate, 'HH:mm')}`;
    }
    return `${format(startDate, 'MMM d HH:mm')} - ${format(endDate, 'MMM d HH:mm')}`;
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad the start of the month to align with weekdays
  const startDay = monthStart.getDay();
  const paddedDays = Array(startDay).fill(null);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="resident-calendar-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(getBackPath())}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Calendar</h1>
              <p className="text-xs text-slate-500">Events & Bookings</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canManageEvents && (
              <Button 
                size="sm"
                onClick={() => {
                  resetEventForm();
                  setSelectedItem(null);
                  setShowEventDialog(true);
                }}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="add-event-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Event
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* View Toggle & Filter */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList className="bg-white border">
              <TabsTrigger value="calendar">
                <CalendarDays className="w-4 h-4 mr-1" />
                Calendar
              </TabsTrigger>
              <TabsTrigger value="list">
                <List className="w-4 h-4 mr-1" />
                List
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[140px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Items</SelectItem>
              <SelectItem value="event">Events Only</SelectItem>
              <SelectItem value="booking">Bookings Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {viewMode === 'calendar' ? (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Calendar Grid */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <h2 className="text-lg font-semibold">
                    {format(currentMonth, 'MMMM yyyy')}
                  </h2>
                  <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {paddedDays.map((_, idx) => (
                    <div key={`pad-${idx}`} className="aspect-square" />
                  ))}
                  {calendarDays.map(day => {
                    const dayItems = getItemsForDate(day).filter(item => 
                      filterType === 'all' || item.type === filterType
                    );
                    const isSelected = isSameDay(day, selectedDate);
                    const isCurrentDay = isToday(day);
                    
                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => setSelectedDate(day)}
                        className={`aspect-square p-1 rounded-lg transition-colors relative ${
                          isSelected 
                            ? 'bg-sky-100 ring-2 ring-sky-500' 
                            : isCurrentDay
                              ? 'bg-slate-100'
                              : 'hover:bg-slate-50'
                        }`}
                      >
                        <span className={`text-sm ${
                          isCurrentDay ? 'font-bold text-sky-600' : ''
                        }`}>
                          {format(day, 'd')}
                        </span>
                        {dayItems.length > 0 && (
                          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                            {dayItems.slice(0, 3).map((item, idx) => (
                              <div 
                                key={idx}
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ backgroundColor: item.color }}
                              />
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-xs text-slate-500">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-purple-500" />
                    <span>Events</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span>Confirmed Bookings</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <span>Pending</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Selected Day Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {format(selectedDate, 'EEEE, MMMM d')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedDateItems.filter(item => 
                  filterType === 'all' || item.type === filterType
                ).length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Calendar className="w-10 h-10 mx-auto mb-2" />
                    <p className="text-sm">No events or bookings</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedDateItems.filter(item => 
                      filterType === 'all' || item.type === filterType
                    ).map(item => (
                      <div 
                        key={item.id}
                        className="p-3 bg-slate-50 rounded-lg border-l-4 cursor-pointer hover:bg-slate-100 transition-colors"
                        style={{ borderLeftColor: item.color }}
                        onClick={() => canManageEvents && item.type === 'event' && openEditDialog(item)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{item.title}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                              <Clock className="w-3 h-3" />
                              <span>{formatTime(item.start)} - {formatTime(item.end)}</span>
                            </div>
                            {item.location && (
                              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                <MapPin className="w-3 h-3" />
                                <span>{item.location}</span>
                              </div>
                            )}
                          </div>
                          {item.type === 'event' ? (
                            getEventTypeBadge(item.event_type)
                          ) : (
                            getStatusBadge(item.status)
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          /* List View */
          <div className="space-y-4">
            {filteredItems.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-semibold text-slate-700">No Upcoming Items</h3>
                  <p className="text-sm text-slate-500 mt-1">Events and bookings will appear here</p>
                </CardContent>
              </Card>
            ) : (
              filteredItems.map(item => (
                <Card key={item.id} className="overflow-hidden">
                  <div 
                    className="w-full h-1"
                    style={{ backgroundColor: item.color }}
                  />
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${item.color}20` }}
                        >
                          {item.type === 'booking' ? (
                            item.booking_type === 'visitor_parking' ? (
                              <Car className="w-6 h-6" style={{ color: item.color }} />
                            ) : (
                              <Building2 className="w-6 h-6" style={{ color: item.color }} />
                            )
                          ) : (
                            <Calendar className="w-6 h-6" style={{ color: item.color }} />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{item.title}</h3>
                          <p className="text-sm text-slate-500 mt-1">
                            {formatDateRange(item.start, item.end)}
                          </p>
                          {item.location && (
                            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {item.location}
                            </p>
                          )}
                          {item.description && (
                            <p className="text-sm text-slate-600 mt-2">{item.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {item.type === 'event' ? (
                          <>
                            {getEventTypeBadge(item.event_type)}
                            {canManageEvents && (
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  onClick={() => openEditDialog(item)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8 text-red-500 hover:text-red-700"
                                  onClick={() => {
                                    setSelectedItem(item);
                                    setShowDeleteConfirm(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {getStatusBadge(item.status)}
                            {item.resident_name && (
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {item.resident_name}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </main>

      {/* Create/Edit Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-sky-600" />
              {selectedItem ? 'Edit Event' : 'Create Event'}
            </DialogTitle>
            <DialogDescription>
              {selectedItem ? 'Update event details' : 'Add a new event to the calendar'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Event Title *</Label>
              <Input
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="e.g., Community BBQ"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Event Type</Label>
              <Select
                value={eventForm.event_type}
                onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="agm">AGM</SelectItem>
                  <SelectItem value="community">Community</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="security_drill">Security Drill</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={eventForm.start_time}
                  onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={eventForm.end_time}
                  onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Location</Label>
              <Input
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                placeholder="e.g., Clubhouse"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="Event details..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEventDialog(false); setSelectedItem(null); resetEventForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={selectedItem ? handleUpdateEvent : handleCreateEvent}
              disabled={submitting}
              className="bg-sky-600 hover:bg-sky-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedItem ? 'Update Event' : 'Create Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Event
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedItem?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setSelectedItem(null); }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteEvent}
              disabled={submitting}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
